import axios from "axios";
import Organization from "../models/serviceModels/Organization";
import UpdateOrganizationRequest from "../models/serviceModels/UpdateOrganizationRequest";
import CreateOrganizationPlanRequest from "../models/serviceModels/CreateOrganizationPlanRequest";
import { DataExport, DataExportDatasetDefinition } from "../models/serviceModels/DataExport";
import { getItem, removeItem, setItem } from "./cacheService";

const baseUrl = process.env.REACT_APP_API_BASE_URL;
const CONFIGURED_REQUIRED_FIELDS_CACHE_KEY = 'configured-required-fields';
const CONFIGURED_REQUIRED_FIELDS_CACHE_TTL_MS = 1000 * 60 * 5;

export interface ConfiguredRequiredField {
    id: string;
    organizationId: string;
    fieldName: string;
    effectiveDate?: string | null;
}

export interface UpdateConfiguredRequiredFieldRequest {
    fieldName: string;
    enabled: boolean;
    effectiveDate?: string | null;
}

const getOrganization = async (): Promise<Organization> => {
    const result = await axios.get(`${baseUrl}/organizations`);
    return result.data;
};

const updateOrganization = async (request: UpdateOrganizationRequest): Promise<Organization> => {
    const result = await axios.put(`${baseUrl}/organizations`, request);
    return result.data;
};

const answerTermsOfService = async (): Promise<Organization> => {
    const result = await axios.post(`${baseUrl}/organizations/tos`, null);
    return result.data;
};

const createOrganizationPlan = async (request: CreateOrganizationPlanRequest): Promise<void> => {
    await axios.post(`${baseUrl}/organizations/plan`, request)
}

const getOrganizationConfiguredRequiredFields = async (): Promise<ConfiguredRequiredField[]> => {
    const cachedFields = getItem<ConfiguredRequiredField[]>(CONFIGURED_REQUIRED_FIELDS_CACHE_KEY);
    if (cachedFields) return cachedFields;

    const result = await axios.get(`${baseUrl}/organizations/configured-required-fields`);
    setItem<ConfiguredRequiredField[]>(CONFIGURED_REQUIRED_FIELDS_CACHE_KEY, result.data, CONFIGURED_REQUIRED_FIELDS_CACHE_TTL_MS);
    return result.data;
};

const updateOrganizationConfiguredRequiredField = async (request: UpdateConfiguredRequiredFieldRequest): Promise<ConfiguredRequiredField | null> => {
    const result = await axios.put(`${baseUrl}/organizations/configured-required-fields`, request);
    removeItem(CONFIGURED_REQUIRED_FIELDS_CACHE_KEY);
    return result.data;
};

const getDataExportDatasets = async (): Promise<DataExportDatasetDefinition[]> => {
    const result = await axios.get(`${baseUrl}/organizations/data-exports/datasets`);
    return result.data;
};

const getDataExports = async (skip = 0, take = 25, sortBy?: string, sortDirection?: 'asc' | 'desc'): Promise<DataExport[]> => {
    const result = await axios.get(`${baseUrl}/organizations/data-exports`, {
        params: {
            skip,
            take,
            sortBy,
            sortDirection
        }
    });
    return result.data;
};

const getDataExport = async (dataExportId: string): Promise<DataExport> => {
    const result = await axios.get(`${baseUrl}/organizations/data-exports/${dataExportId}`);
    return result.data;
};

const createDataExport = async (
    datasetIds: string[],
    dateRangeStart?: string | null,
    dateRangeEnd?: string | null
): Promise<DataExport> => {
    const result = await axios.post(`${baseUrl}/organizations/data-exports`, {
        datasetIds,
        dateRangeStart: dateRangeStart || null,
        dateRangeEnd: dateRangeEnd || null
    });
    return result.data;
};

const cancelDataExport = async (dataExportId: string): Promise<DataExport> => {
    const result = await axios.post(`${baseUrl}/organizations/data-exports/${dataExportId}/cancel`);
    return result.data;
};

const forceCancelDataExport = async (dataExportId: string): Promise<DataExport> => {
    const result = await axios.post(`${baseUrl}/organizations/data-exports/${dataExportId}/force-cancel`);
    return result.data;
};

export {
    getOrganization,
    updateOrganization,
    answerTermsOfService,
    createOrganizationPlan,
    getOrganizationConfiguredRequiredFields,
    updateOrganizationConfiguredRequiredField,
    getDataExportDatasets,
    getDataExports,
    getDataExport,
    createDataExport,
    cancelDataExport,
    forceCancelDataExport
};
